import React, { useState, useEffect } from 'react';
import { CheckCircle, FileText, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { contentService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const Employers = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getText, isEnglish } = useLanguage();

  // Default values - Chinese
  const defaultRequirements = [
    {
      title: '僱主資格',
      items: ['香港永久性居民或香港居民', '年滿18歲', '擁有香港住址證明', '有穩定收入']
    },
    {
      title: '住宿要求',
      items: ['提供獨立房間', '合理的生活設施', '安全及衞生的環境', '合符法例要求']
    },
    {
      title: '費用負擔',
      items: ['每月工資：港幣$5,195或以上', '膳食津貼：港幣$1,236或提供膳食', '免費住宿', '來回機票及保險']
    }
  ];

  // Default values - English
  const defaultRequirementsEn = [
    {
      title: 'Employer Qualifications',
      items: ['Hong Kong permanent resident or resident', 'At least 18 years old', 'Proof of Hong Kong address', 'Stable income']
    },
    {
      title: 'Accommodation Requirements',
      items: ['Provide separate room', 'Reasonable living facilities', 'Safe and hygienic environment', 'Comply with legal requirements']
    },
    {
      title: 'Financial Obligations',
      items: ['Monthly salary: HK$5,195 or above', 'Food allowance: HK$1,236 or provide meals', 'Free accommodation', 'Round-trip airfare and insurance']
    }
  ];

  const defaultDocuments = [
    '香港身份證副本',
    '住址證明文件',
    '收入證明',
    '僱主聲明書',
    '住所平面圖（如需要）'
  ];

  const defaultDocumentsEn = [
    'Copy of Hong Kong ID card',
    'Proof of address document',
    'Proof of income',
    'Employer declaration form',
    'Floor plan of residence (if required)'
  ];

  const defaultResponsibilities = [
    { icon: '📄', title: '合約義務', description: '遵守僱傭合約條款，按時支付工資及相關費用' },
    { icon: '🛡️', title: '保險責任', description: '為外傭購買法定保險，保障雙方權益' },
    { icon: '👥', title: '合理待遇', description: '按法例給予休息日、有薪假期及合理工時' }
  ];

  const defaultResponsibilitiesEn = [
    { icon: '📄', title: 'Contract Obligations', description: 'Comply with employment contract terms, pay wages and related expenses on time' },
    { icon: '🛡️', title: 'Insurance Responsibility', description: 'Purchase statutory insurance for helpers to protect both parties' },
    { icon: '👥', title: 'Fair Treatment', description: 'Provide rest days, paid leave, and reasonable working hours as required by law' }
  ];

  const defaultTips = [
    { title: '明確期望', content: '清楚說明工作要求及期望，避免誤會' },
    { title: '良好溝通', content: '保持開放态度，及時溝通任何問題' },
    { title: '尊重對方', content: '尊重外傭的文化背景及個人隐私' },
    { title: '定期評估', content: '定期評估工作表現，給予正面反饋' },
    { title: '提供培訓', content: '適當提供培訓機會，提升工作技能' },
    { title: '合法僱傭', content: '确保所有程序合法，保障僱傭權益' }
  ];

  const defaultTipsEn = [
    { title: 'Set Clear Expectations', content: 'Clearly explain job requirements and expectations to avoid misunderstandings' },
    { title: 'Good Communication', content: 'Maintain an open attitude and communicate any issues promptly' },
    { title: 'Show Respect', content: 'Respect the helper\'s cultural background and personal privacy' },
    { title: 'Regular Evaluation', content: 'Regularly evaluate job performance and provide positive feedback' },
    { title: 'Provide Training', content: 'Provide appropriate training opportunities to enhance work skills' },
    { title: 'Legal Employment', content: 'Ensure all procedures are legal to protect employment rights' }
  ];

  const defaultLegalInfo = [
    '最低工資：2025年外傭最低工資為港幣$5,195。',
    '休息日：每星期至少1天休息日，如工作需按法例計算資銀。',
    '法定假期：每年享有12天法定假期。',
    '有薪年假：合約每滿12個月享有7天有薪年假。',
    '保險要求：必須購買僱員補償保險及醫療保險。',
    '終止合約：需按合約約定給予通知期或代通知期薪金。'
  ];

  const defaultLegalInfoEn = [
    'Minimum Wage: The 2025 minimum wage for foreign domestic helpers is HK$5,195.',
    'Rest Days: At least 1 rest day per week; if work is required, wages must be calculated according to law.',
    'Statutory Holidays: 12 statutory holidays per year.',
    'Annual Leave: 7 days paid annual leave for every 12 months of contract completion.',
    'Insurance Requirements: Employee compensation insurance and medical insurance are mandatory.',
    'Contract Termination: Notice period or payment in lieu of notice as specified in the contract is required.'
  ];

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await contentService.get();
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get requirements with correct language
  const getRequirements = () => {
    if (!content?.employerRequirements || content.employerRequirements.length === 0) {
      return isEnglish ? defaultRequirementsEn : defaultRequirements;
    }
    
    if (isEnglish) {
      return content.employerRequirements.map((req, idx) => ({
        title: req.titleEn || defaultRequirementsEn[idx]?.title || req.title,
        items: req.itemsEn?.length > 0 ? req.itemsEn : (defaultRequirementsEn[idx]?.items || req.items)
      }));
    }
    return content.employerRequirements;
  };
  
  // Get documents with correct language
  const getDocuments = () => {
    if (!content?.employerDocuments || content.employerDocuments.length === 0) {
      return isEnglish ? defaultDocumentsEn : defaultDocuments;
    }
    
    if (isEnglish) {
      if (content.employerDocumentsEn && content.employerDocumentsEn.length > 0) {
        return content.employerDocumentsEn.map((en, idx) => en || defaultDocumentsEn[idx] || content.employerDocuments[idx]);
      }
      return defaultDocumentsEn;
    }
    return content.employerDocuments;
  };
  
  // Get responsibilities with correct language
  const getResponsibilities = () => {
    if (!content?.employerResponsibilities || content.employerResponsibilities.length === 0) {
      return isEnglish ? defaultResponsibilitiesEn : defaultResponsibilities;
    }
    
    if (isEnglish) {
      return content.employerResponsibilities.map((resp, idx) => ({
        icon: resp.icon,
        title: resp.titleEn || defaultResponsibilitiesEn[idx]?.title || resp.title,
        description: resp.descriptionEn || defaultResponsibilitiesEn[idx]?.description || resp.description
      }));
    }
    return content.employerResponsibilities;
  };
  
  // Get tips with correct language
  const getTips = () => {
    if (!content?.employerTips || content.employerTips.length === 0) {
      return isEnglish ? defaultTipsEn : defaultTips;
    }
    
    if (isEnglish) {
      return content.employerTips.map((tip, idx) => ({
        title: tip.titleEn || defaultTipsEn[idx]?.title || tip.title,
        content: tip.contentEn || defaultTipsEn[idx]?.content || tip.content
      }));
    }
    return content.employerTips;
  };
  
  // Get legal info with correct language
  const getLegalInfo = () => {
    if (!content?.employerLegalInfo || content.employerLegalInfo.length === 0) {
      return isEnglish ? defaultLegalInfoEn : defaultLegalInfo;
    }
    
    if (isEnglish) {
      if (content.employerLegalInfoEn && content.employerLegalInfoEn.length > 0) {
        return content.employerLegalInfoEn.map((en, idx) => en || defaultLegalInfoEn[idx] || content.employerLegalInfo[idx]);
      }
      return defaultLegalInfoEn;
    }
    return content.employerLegalInfo;
  };

  const requirements = getRequirements();
  const documents = getDocuments();
  const responsibilities = getResponsibilities();
  const tips = getTips();
  const legalInfo = getLegalInfo();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#00b6a5' }} />
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 to-lime-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">
              <span style={{ color: '#00b6a5' }}>{getText('僱主須知', 'Employer Information')}</span>
            </h1>
            <p className="text-xl text-gray-600">{getText('了解您的權利與責任', 'Understand Your Rights and Responsibilities')}</p>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              <span style={{ color: '#00b6a5' }}>{getText('僱主基本要求', 'Basic Employer Requirements')}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {requirements.map((req, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle style={{ color: '#00b6a5' }}>{req.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {req.items.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#00b6a5' }} />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Documents */}
      <section className="py-16" style={{ backgroundColor: '#f8fffe' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              <span style={{ color: '#00b6a5' }}>{getText('所需文件', 'Required Documents')}</span>
            </h2>
            <p className="text-gray-600">{getText('請準備以下文件以便辦理申請', 'Please prepare the following documents for application')}</p>
          </div>

          <Card>
            <CardContent className="pt-6">
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {documents.map((doc, index) => (
                  <li key={index} className="flex items-start p-4 rounded-lg hover:bg-gray-50 transition-colors">
                    <FileText className="h-6 w-6 mr-3 mt-0.5 flex-shrink-0" style={{ color: '#00b6a5' }} />
                    <span className="text-lg text-gray-700">{doc}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Responsibilities */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              <span style={{ color: '#00b6a5' }}>{getText('僱主責任', 'Employer Responsibilities')}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {responsibilities.map((resp, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-8 pb-6">
                  <div className="flex justify-center mb-4">
                    <span className="text-5xl">{resp.icon}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: '#00b6a5' }}>
                    {resp.title}
                  </h3>
                  <p className="text-gray-600">{resp.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Tips */}
      <section className="py-16" style={{ backgroundColor: '#f8fffe' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              <span style={{ color: '#00b6a5' }}>{getText('良好僱傭關係要訣', 'Tips for Good Employment Relations')}</span>
            </h2>
            <p className="text-gray-600">{getText('建立和諧的僱傭關係', 'Build a harmonious employment relationship')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tips.map((tip, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start">
                    <CheckCircle className="h-6 w-6 mr-3 mt-1 flex-shrink-0" style={{ color: '#00b6a5' }} />
                    <div>
                      <h3 className="text-lg font-bold mb-2" style={{ color: '#00b6a5' }}>
                        {tip.title}
                      </h3>
                      <p className="text-gray-600">{tip.content}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Information */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2" style={{ borderColor: '#00b6a5' }}>
            <CardHeader style={{ backgroundColor: '#f8fffe' }}>
              <CardTitle className="text-2xl" style={{ color: '#00b6a5' }}>{getText('法例資訊', 'Legal Information')}</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4 text-gray-700">
                {legalInfo.map((info, index) => (
                  <p key={index}><strong>•</strong> {info}</p>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ background: 'linear-gradient(135deg, #00b6a5 0%, #00d4bd 100%)' }}>
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6">
            還有疑問？
          </h2>
          <p className="text-xl text-white mb-8">
            我們的專業團隊隨時為您解答
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`https://api.whatsapp.com/send?phone=${(content?.whatsapp || '85261871388').replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-white px-8 py-6 text-lg" style={{ color: '#00b6a5' }}>
                WhatsApp 查詢
              </Button>
            </a>
            <a href={`tel:${(content?.phone || '+852 6187 1388').replace(/\s/g, '')}`}>
              <Button size="lg" className="px-8 py-6 text-lg" style={{ backgroundColor: '#00b6a5', color: '#000' }}>
                致電查詢
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Employers;
